import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Mail, Phone, Loader2, CheckCircle } from 'lucide-react';
import { submitContact } from '../services/api';

export const ContactSection = () => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    btw: '',
    message: '',
    gdpr: false
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.gdpr) {
      toast.error('GDPR toestemming vereist', {
        description: 'Gelieve akkoord te gaan met de privacyvoorwaarden.'
      });
      return;
    }
    
    try {
      setSubmitting(true);
      await submitContact(formData);
      
      // Show inline success instead of toast
      setSuccess(true);
      
    } catch (error) {
      toast.error('Fout bij verzenden', {
        description: 'Probeer het later opnieuw of neem direct contact op.'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleReset = () => {
    setSuccess(false);
    setFormData({
      type: '',
      name: '',
      email: '',
      phone: '',
      company: '',
      btw: '',
      message: '',
      gdpr: false
    });
  };
  
  const isB2B = formData.type === 'professional' || formData.type === 'retail';
  
  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-sweet-sans text-3xl sm:text-4xl font-normal text-earth-brown mb-6 uppercase tracking-wide">
              Neem contact op
            </h2>
            <p className="font-sweet-sans text-sm text-almost-black/80">
              Vragen over onze producten of interesse in een B2B samenwerking?
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="border-terra-beige/20 bg-white">
                <CardHeader>
                  <CardTitle className="font-sweet-sans text-xl text-earth-brown uppercase tracking-wide">Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-terra-beige mt-1" />
                    <div>
                      <p className="font-sweet-sans text-sm text-almost-black/60 mb-1">Email</p>
                      <a href="mailto:info@l-art.be" className="font-sweet-sans text-sm text-earth-brown hover:text-terra-beige">
                        info@l-art.be
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-terra-beige mt-1" />
                    <div>
                      <p className="font-sweet-sans text-sm text-almost-black/60 mb-1">Telefoon</p>
                      <a href="tel:+32471983404" className="font-sweet-sans text-sm text-earth-brown hover:text-terra-beige">
                        +32 471 98 34 04
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-terra-beige/20 bg-gradient-to-br from-terra-beige/5 to-terra-beige/10">
                <CardContent className="pt-6">
                  <p className="font-sweet-sans text-sm text-almost-black/70 leading-relaxed">
                    <strong className="text-earth-brown">Professionals:</strong> Voor stalen, offertes en B2B voorwaarden, 
                    selecteer &quot;Interieurprofessional&quot; of &quot;Retail&quot; in het formulier.
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-terra-beige/20 bg-white">
                <CardContent className="pt-6">
                  {success ? (
                    /* Success State */
                    <div className="py-8 text-center" role="status" aria-live="polite">
                      <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                      </div>
                      <h3 className="font-sweet-sans text-xl font-semibold text-earth-brown mb-3">
                        Bericht verzonden
                      </h3>
                      <p className="font-sweet-sans text-almost-black/70 mb-2">
                        Bedankt voor uw bericht.
                      </p>
                      <p className="font-sweet-sans text-almost-black/70 mb-6">
                        We antwoorden meestal binnen 1 à 2 werkdagen.
                      </p>
                      <button
                        type="button"
                        onClick={handleReset}
                        className="font-sweet-sans text-sm text-earth-brown hover:text-terra-beige underline"
                      >
                        Nieuw bericht versturen
                      </button>
                    </div>
                  ) : (
                    /* Form State */
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div>
                        <Label htmlFor="type" className="font-sweet-sans text-almost-black mb-2">Ik ben...</Label>
                        <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                          <SelectTrigger className="border-terra-beige/30 focus:ring-terra-beige">
                            <SelectValue placeholder="Selecteer uw type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="particulier">Particulier</SelectItem>
                          <SelectItem value="professional">Interieurprofessional</SelectItem>
                          <SelectItem value="retail">Retail</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="font-sweet-sans text-almost-black mb-2">Naam *</Label>
                        <Input 
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="border-terra-beige/30 focus:ring-terra-beige"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email" className="font-sweet-sans text-almost-black mb-2">Email *</Label>
                        <Input 
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="border-terra-beige/30 focus:ring-terra-beige"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="font-sweet-sans text-almost-black mb-2">Telefoon</Label>
                      <Input 
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="border-terra-beige/30 focus:ring-terra-beige"
                      />
                    </div>
                    
                    {isB2B && (
                      <div className="grid sm:grid-cols-2 gap-4 p-4 bg-terra-beige/5  border border-terra-beige/20">
                        <div>
                          <Label htmlFor="company" className="font-sweet-sans text-almost-black mb-2">Bedrijf</Label>
                          <Input 
                            id="company"
                            value={formData.company}
                            onChange={(e) => setFormData({...formData, company: e.target.value})}
                            className="border-terra-beige/30 focus:ring-terra-beige bg-white"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="btw" className="font-sweet-sans text-almost-black mb-2">BTW nummer</Label>
                          <Input 
                            id="btw"
                            value={formData.btw}
                            onChange={(e) => setFormData({...formData, btw: e.target.value})}
                            className="border-terra-beige/30 focus:ring-terra-beige bg-white"
                          />
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <Label htmlFor="message" className="font-sweet-sans text-almost-black mb-2">Bericht *</Label>
                      <Textarea 
                        id="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="border-terra-beige/30 focus:ring-terra-beige resize-none"
                        placeholder="Vertel ons over uw project of vraag..."
                      />
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <Checkbox 
                        id="gdpr"
                        checked={formData.gdpr}
                        onCheckedChange={(checked) => setFormData({...formData, gdpr: checked})}
                        className="mt-1 border-terra-beige/50 data-[state=checked]:bg-terra-beige"
                      />
                      <Label htmlFor="gdpr" className="font-sweet-sans text-sm text-almost-black/70 leading-relaxed cursor-pointer">
                        Ik ga akkoord met de verwerking van mijn gegevens conform de privacyvoorwaarden *
                      </Label>
                    </div>
                    
                    <button 
                      type="submit"
                      disabled={submitting}
                      className="w-full px-6 py-3 bg-earth-brown hover:bg-earth-brown/90 text-white font-sweet-sans text-sm uppercase tracking-wider transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verzenden...
                        </span>
                      ) : (
                        'Verstuur aanvraag'
                      )}
                    </button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};